import { NotificationTopicOwnersSettingsSection } from "@/components/NotificationTopicOwnersSettingsSection";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

const NOTIFICATION_STAFF_USERS_QUERY_KEY = ["notifications", "staff-user-options"] as const;

function fetchNotificationStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/notifications/staff-user-options");
}

export function AdminSettingsPage() {
  const canEdit = useHubModuleAdmin("admin");

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Admin module settings: support ticket topic owners and other hub-wide admin configuration.">
          Admin · Settings
        </PageTitleWithHelp>
      </header>

      <NotificationTopicOwnersSettingsSection
        topic="support_ticket"
        title="Support ticket topic owners"
        hoverHint="Hub users in this topic owner group receive in-app notifications for new support tickets, and may claim ownership of open tickets."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Admin module administrators can edit this topic owner group."
      />

      <NotificationTopicOwnersSettingsSection
        topic="gemini_document_extract_spend"
        title="GCP spend alerts"
        hoverHint="Hub users in this group get a daily email and in-app note with the actual Google Cloud billing export, including the Default Gemini Project total."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Admin module administrators can edit this topic owner group."
      />

      <NotificationTopicOwnersSettingsSection
        topic="ops_sidecar_down"
        title="Sidecar outage alerts"
        hoverHint="Hub users in this distribution group receive email and in-app notifications when an independent production sidecar cannot be revived, and when it recovers."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Admin module administrators can edit this topic owner group."
      />

      <NotificationTopicOwnersSettingsSection
        topic="megaventory_qbo_housekeeping"
        title="Megaventory monthly QuickBooks task"
        hoverHint="On the first morning of each month, hub users in this group get an email and an inbox item: update the Megaventory USD to CNY rate, then clear the QuickBooks Online error log."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Admin module administrators can edit this topic owner group."
      />

      <NotificationTopicOwnersSettingsSection
        topic="ops_database_stuck_session"
        title="Stuck database session alerts"
        hoverHint="Hub users in this group get at most one email and in-app note per day when a production database session is stuck on a lock it already holds and ignores cancel and terminate. Clearing it needs a Supabase restart outside production hours."
        canEdit={canEdit}
        userOptionsQueryKey={NOTIFICATION_STAFF_USERS_QUERY_KEY}
        fetchUserOptions={fetchNotificationStaffUsers}
        readOnlyHoverHint="Admin module administrators can edit this topic owner group."
      />
    </div>
  );
}
